import { computed, ref } from "vue";
import type { useHeaderMenuRoutes } from "./useHeaderMenuRoutes";

export type MobileMenuPage = {
  key: string;
  type: string;
  title?: string;
  category?: any;
};

const MOBILE_MENU_CLOSE_DISTANCE = 72;
const MOBILE_MENU_AXIS_THRESHOLD = 8;

export function useMobileDrillMenu(deps: {
  routes: ReturnType<typeof useHeaderMenuRoutes>;
  closeMobileMenu: () => void;
  handleMobileMenuLinkNavigation: (to: any) => void;
}) {
  const { routes, closeMobileMenu, handleMobileMenuLinkNavigation } = deps;
  const {
    normalizeName,
    toRoute,
    findCategoryByName,
    findChildCategoryByName,
    primaryCategory,
    finalSaleCategory,
    brandSubcategories,
    categoryFilterRoute,
    categorySearchRoute,
    bestSellersRoute,
    newArrivalsRoute,
    optionFilterByGroup,
    shapeFilterByName,
    mobileMenuColors,
    featureLinksForCategory,
    exactShapeNamesForCategory,
    exactBrandNamesForCategory,
    brandFilterLinkByName,
  } = routes;

  let mobileMenuTouchStartX = 0;
  let mobileMenuTouchStartY = 0;
  let mobileMenuSwipeAxis: "x" | "y" | null = null;

  const mobileStack = ref<MobileMenuPage[]>([
    { key: "root", type: "root", title: "" },
  ]);
  const mobileMenuDirection = ref<"forward" | "back">("forward");
  const mobileSearchQuery = ref("");
  const mobileBrandSearch = ref("");
  const mobilePageScrollPositions = ref<Record<string, number>>({});

  const mobileCurrentPage = computed(
    () => mobileStack.value[mobileStack.value.length - 1]
  );

  const finalSaleRoute = computed(
    () => toRoute(finalSaleCategory.value?.url) || "/final-sale-eyewear"
  );

  const mobileBackLabel = computed(() => {
    const previous = mobileStack.value[mobileStack.value.length - 2];
    return previous?.title || "Back";
  });

  const mobileMenuTransitionName = computed(() =>
    mobileMenuDirection.value === "back"
      ? "mobile-drill-back"
      : "mobile-drill-forward"
  );

  const saveMobilePageScroll = (element: Element) => {
    const page = element as HTMLElement;
    const pageKey = page.dataset.mobilePageKey;
    if (!pageKey) return;

    mobilePageScrollPositions.value = {
      ...mobilePageScrollPositions.value,
      [pageKey]: page.scrollTop,
    };
  };

  const saveActiveMobilePageScroll = () => {
    if (typeof document === "undefined") return;

    const activePage = document.querySelector(
      ".mobile-drill-menu__page:not(.mobile-drill-forward-leave-active):not(.mobile-drill-back-leave-active)"
    );

    if (activePage) {
      saveMobilePageScroll(activePage);
    }
  };

  const restoreMobilePageScroll = (element: Element) => {
    const page = element as HTMLElement;
    const pageKey = page.dataset.mobilePageKey;
    if (!pageKey) return;

    page.scrollTop = mobilePageScrollPositions.value[pageKey] || 0;
  };

  const handleMobilePageScroll = (event: Event) => {
    saveMobilePageScroll(event.currentTarget as Element);
  };

  const mobilePush = (page: MobileMenuPage) => {
    saveActiveMobilePageScroll();
    mobileMenuDirection.value = "forward";
    mobileStack.value = [...mobileStack.value, page];
  };

  const mobileBack = () => {
    if (mobileStack.value.length <= 1) return;
    saveActiveMobilePageScroll();
    mobileMenuDirection.value = "back";
    mobileStack.value = mobileStack.value.slice(0, -1);
  };

  const resetMobileMenu = () => {
    mobileMenuDirection.value = "back";
    mobileStack.value = [{ key: "root", type: "root", title: "" }];
    mobileSearchQuery.value = "";
    mobileBrandSearch.value = "";
    mobilePageScrollPositions.value = {};
  };

  const handleMobileSearchSubmit = () => {
    const search = mobileSearchQuery.value.trim();
    const category = primaryCategory.value;
    if (!search || !category?.url) return;

    const destination = categorySearchRoute(category, search);
    if (destination) handleMobileMenuLinkNavigation(destination);
  };

  const handleMobileMenuTouchStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    mobileMenuTouchStartX = touch.clientX;
    mobileMenuTouchStartY = touch.clientY;
    mobileMenuSwipeAxis = null;
  };

  const handleMobileMenuTouchMove = (event: TouchEvent) => {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const dx = touch.clientX - mobileMenuTouchStartX;
    const dy = touch.clientY - mobileMenuTouchStartY;

    if (
      !mobileMenuSwipeAxis &&
      (Math.abs(dx) > MOBILE_MENU_AXIS_THRESHOLD ||
        Math.abs(dy) > MOBILE_MENU_AXIS_THRESHOLD)
    ) {
      mobileMenuSwipeAxis = Math.abs(dx) > Math.abs(dy) * 1.25 ? "x" : "y";
    }

    if (mobileMenuSwipeAxis === "x" && dx < 0) {
      event.preventDefault();
    }
  };

  const handleMobileMenuTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - mobileMenuTouchStartX;

    if (mobileMenuSwipeAxis === "x" && dx < -MOBILE_MENU_CLOSE_DISTANCE) {
      closeMobileMenu();
    }

    mobileMenuSwipeAxis = null;
  };

  const mobileCurrentListItems = computed(() => {
    const page = mobileCurrentPage.value;
    const category = page.category;

    if (page.type === "shape") {
      return exactShapeNamesForCategory(category)
        .map((shapeName) => {
          const shape = shapeFilterByName(shapeName);
          return {
            key: `shape-${shape?.filter || shapeName}`,
            label: shapeName,
            image: shape?.image,
            to: categoryFilterRoute(category, shape?.filter, shapeName),
          };
        })
        .filter((item) => Boolean(item.to));
    }

    if (page.type === "color") {
      return mobileMenuColors.value
        .map((color: any) => ({
          ...color,
          to: categoryFilterRoute(category, color.filter, color.label),
        }))
        .filter((item: any) => Boolean(item.to));
    }

    if (page.type === "feature") {
      return featureLinksForCategory(category);
    }

    if (page.type === "brand-filter") {
      const brandLinks = exactBrandNamesForCategory(category)
        .map((brandName) => brandFilterLinkByName(category, brandName))
        .filter((item) => Boolean(item?.to));

      return [
        ...brandLinks,
        {
          key: "view-all-brands",
          label: "View All Brands",
          to: "/brands",
        },
      ];
    }

    return [];
  });

  const mobileCategoryLinks = computed(() => {
    const category = mobileCurrentPage.value.category;
    const isEyeglasses = normalizeName(category?.name).includes("eyeglass");
    const womenCategory = findChildCategoryByName(
      category,
      "women's collections",
      "women"
    );
    const menCategory = findChildCategoryByName(
      category,
      "men's collections",
      "men"
    );
    const polarizedFilter = optionFilterByGroup("polarized", "yes");
    const blueLightFilter =
      optionFilterByGroup("lens type", "blue light lenses") ||
      optionFilterByGroup("lens color", "blue light lenses");
    const links = [
      {
        key: "shop-all",
        label: "Shop All",
        to: toRoute(category?.url),
      },
      {
        key: "best-sellers",
        label: "Best Sellers",
        to: bestSellersRoute(category),
      },
      {
        key: "new-arrivals",
        label: "New Arrivals",
        badge: "new",
        to: newArrivalsRoute(category),
      },
      {
        key: "women",
        label: "Women's",
        to: womenCategory
          ? toRoute(womenCategory.url)
          : categorySearchRoute(category, "women"),
      },
      {
        key: "men",
        label: "Men's",
        to: menCategory
          ? toRoute(menCategory.url)
          : categorySearchRoute(category, "men"),
      },
    ];

    if (isEyeglasses) {
      links.push(
        {
          key: "blue-light",
          label: "Blue Light",
          to: categoryFilterRoute(
            category,
            blueLightFilter?.filter,
            "Blue Light"
          ),
        },
        {
          key: "final-sale",
          label: "Final Sale",
          sale: true,
          to: finalSaleRoute.value,
        }
      );
      return links.filter((item) => Boolean(item.to));
    }

    links.push(
      {
        key: "polarized",
        label: "Polarized",
        to: categoryFilterRoute(category, polarizedFilter?.filter, "Polarized"),
      },
      {
        key: "final-sale",
        label: "Final Sale",
        sale: true,
        to: finalSaleRoute.value,
      }
    );

    return links.filter((item) => Boolean(item.to));
  });

  const filteredManufacturerGroups = computed(() => {
    const query = normalizeName(mobileBrandSearch.value);
    const list = query
      ? brandSubcategories.value.filter((cat: any) =>
          normalizeName(cat.name).includes(query)
        )
      : brandSubcategories.value;

    const groups: { letter: string; brands: any[] }[] = [];
    let currentLetter = "";

    for (const brand of list) {
      const first = (brand.name || "").charAt(0).toUpperCase();
      const letter = /\d/.test(first) ? "0-9" : first;
      if (letter !== currentLetter) {
        currentLetter = letter;
        groups.push({ letter, brands: [] });
      }
      groups[groups.length - 1].brands.push(brand);
    }

    return groups;
  });

  return {
    mobileStack,
    mobileMenuDirection,
    mobileSearchQuery,
    mobileBrandSearch,
    mobilePageScrollPositions,
    mobileCurrentPage,
    mobileBackLabel,
    mobileMenuTransitionName,
    saveMobilePageScroll,
    restoreMobilePageScroll,
    handleMobilePageScroll,
    mobilePush,
    mobileBack,
    resetMobileMenu,
    handleMobileSearchSubmit,
    handleMobileMenuTouchStart,
    handleMobileMenuTouchMove,
    handleMobileMenuTouchEnd,
    mobileCurrentListItems,
    mobileCategoryLinks,
    filteredManufacturerGroups,
  };
}
